<script setup lang="ts">
    import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue';
    import type { BadgeType } from '~/@types/cms';

    interface GalleryBadgePopoverProps {
        badge?: BadgeType;
        redCharity?: boolean;
        firstSlide?: boolean;
    }
    const props = defineProps<GalleryBadgePopoverProps>();

    const { t } = useLocales();

    const description = computed(() => {
        if (!props.badge) return;
        return t(`badges.${props.badge}`);
    });

    const a11yLabel = computed(() => {
        if (!props.badge) return;
        return t(`cta.badge.${props.badge}`);
    });
</script>

<template>
    <div class="atm-gallery-badge-popover absolute left-1 right-1 top-1 z-20">
        <popover class="relative">
            <popover-button
                class="absolute right-2 top-2"
                :aria-label="a11yLabel">
                <lazy-woom-icon-red-charity
                    v-if="redCharity && firstSlide"
                    class="h-8 fill-woom-red max-md:h-6" />
                <component
                    v-else
                    :is="badgeIcon(badge)"
                    class="h-12 w-12" />
            </popover-button>
            <transition
                enter-active-class="transition duration-300 ease-out"
                enter-from-class="translate-x-2 opacity-0"
                enter-to-class="translate-x-0 opacity-100"
                leave-active-class="transition duration-300 ease-out"
                leave-from-class="translate-x-0 opacity-100"
                leave-to-class="translate-x-2 opacity-0">
                <popover-panel
                    v-slot="{ close }"
                    class="absolute left-auto right-0 top-0 flex min-h-14 items-center rounded-md bg-woom-grey py-2 pl-6 pr-2 drop-shadow-md md:px-3">
                    <template v-if="redCharity && firstSlide">
                        <div class="flex gap-2 max-md:flex-col">
                            <lazy-woom-icon-red-charity class="h-6 shrink-0 self-start fill-woom-red" />
                            <span>
                                Purchasing our (woom)<sup>RED</sup> bike means you too are joining our mission to bring even more happy childhoods to
                                families around the world. Our commitment of $100,000 will support (RED)’s efforts in bringing life-saving healthcare
                                programs to children in communities most in need.
                            </span>
                        </div>
                    </template>
                    <template v-else>
                        <component
                            :is="badgeIcon(badge)"
                            class="h-12 w-12 shrink-0 self-start" />
                        <span class="px-2 md:px-3">{{ description }}</span>
                    </template>
                    <button
                        class="h-12 w-12 shrink-0 self-start transition-colors hover:text-woom-red"
                        @click="close()">
                        <woom-icon-close
                            v-if="close"
                            :aria-label="t('cta.badge.close')"
                            class="mx-auto h-4 w-4 fill-current" />
                    </button>
                </popover-panel>
            </transition>
        </popover>
    </div>
</template>
